/* CurriculumSkillsPanel — left-side whiteboard panel.
 *
 * Flow:
 *   1. Teacher picks a Subject   (Maths / English / Science)
 *   2. Teacher picks a Grade     (P1–S4)
 *   3. Topic list appears        (curriculum outline for that pair)
 *   4. Search box filters topics by keyword
 *   5. Click a topic → onPractice(skill) fires (parent drops widget on canvas)
 *
 * All content is generated on demand via POST /api/skills/curriculum/practice
 * (cached per teacher × subject × grade × topic).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { BookOpen, Check, ChevronLeft, Loader2, Search, Sparkles, X, Zap, Wand2, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const SUBJECT_FALLBACK_COLORS = { maths: "#7C3AED", english: "#0EA5E9", science: "#10B981" };

export default function CurriculumSkillsPanel({ onPractice, onVisual, onClose }) {
  const [tree, setTree] = useState(null);      // full curriculum tree
  const [treeLoading, setTreeLoading] = useState(false);
  const [subjectKey, setSubjectKey] = useState(null);
  const [gradeKey, setGradeKey] = useState(null);
  const [topics, setTopics] = useState([]);
  const [topicItems, setTopicItems] = useState([]); // [{name, cached}]
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [cachedCount, setCachedCount] = useState(0);
  const [q, setQ] = useState("");
  const [busyTopic, setBusyTopic] = useState(null);
  const [choiceTopic, setChoiceTopic] = useState(null); // topic awaiting SVG-vs-Activity choice
  const [designOpen, setDesignOpen] = useState(false);
  const [designTitle, setDesignTitle] = useState("");
  const [designKinds, setDesignKinds] = useState(["illustration", "quiz"]);
  const [designBusy, setDesignBusy] = useState(false);
  const [warmup, setWarmup] = useState(null); // {job_id, total, done, generated, status}
  const warmupTimerRef = useRef(null);

  // Load subject/grade tree once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setTreeLoading(true);
      try {
        const { data } = await api.get("/skills/curriculum/tree");
        if (!cancelled) setTree(data);
      } catch {
        if (!cancelled) toast.error("Could not load curriculum");
      } finally {
        if (!cancelled) setTreeLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load topics when subject + grade selected
  useEffect(() => {
    if (!subjectKey || !gradeKey) { setTopics([]); setTopicItems([]); setCachedCount(0); return; }
    let cancelled = false;
    (async () => {
      setTopicsLoading(true);
      try {
        const { data } = await api.get("/skills/curriculum/topics", {
          params: { subject: subjectKey, grade: gradeKey },
        });
        if (!cancelled) {
          setTopics(data.topics || []);
          setTopicItems(data.topic_items || (data.topics || []).map((t) => ({ name: t, cached: false })));
          setCachedCount(data.cached_count || 0);
        }
      } catch {
        if (!cancelled) toast.error("Could not load topics");
      } finally {
        if (!cancelled) setTopicsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [subjectKey, gradeKey]);

  const subject = tree?.subjects?.find((s) => s.key === subjectKey);
  const grade = subject?.grades?.find((g) => g.key === gradeKey);
  const filteredTopicItems = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const src = topicItems.length ? topicItems : topics.map((t) => ({ name: t, cached: false }));
    if (!needle) return src;
    return src.filter((t) => t.name.toLowerCase().includes(needle));
  }, [topicItems, topics, q]);

  const refreshTopicItems = async () => {
    if (!subjectKey || !gradeKey) return;
    try {
      const { data } = await api.get("/skills/curriculum/topics", {
        params: { subject: subjectKey, grade: gradeKey },
      });
      setTopics(data.topics || []);
      setTopicItems(data.topic_items || (data.topics || []).map((t) => ({ name: t, cached: false })));
      setCachedCount(data.cached_count || 0);
    } catch { /* ignore */ }
  };

  // Reset warmup state whenever grade/subject changes
  useEffect(() => {
    if (warmupTimerRef.current) { clearInterval(warmupTimerRef.current); warmupTimerRef.current = null; }
    setWarmup(null);
  }, [subjectKey, gradeKey]);

  // Poll the warmup job until it finishes
  useEffect(() => {
    if (!warmup?.job_id || warmup.status !== "running") return;
    const tick = async () => {
      try {
        const { data } = await api.get(`/skills/curriculum/warmup/${warmup.job_id}`);
        const j = data.job;
        setWarmup(j);
        if (j.status === "done" || j.status === "error") {
          clearInterval(warmupTimerRef.current);
          warmupTimerRef.current = null;
          await refreshTopicItems();
          if (j.status === "done") {
            toast.success(`Warmed up ${j.generated} topics · ${(j.failed || []).length} failed`);
          } else {
            toast.error(`Warmup errored — ${(j.error || "").slice(0, 80)}`);
          }
        }
      } catch { /* ignore */ }
    };
    warmupTimerRef.current = setInterval(tick, 2500);
    return () => { if (warmupTimerRef.current) { clearInterval(warmupTimerRef.current); warmupTimerRef.current = null; } };
  }, [warmup?.job_id, warmup?.status]);

  const startWarmup = async () => {
    if (warmup?.status === "running") return;
    try {
      const { data } = await api.post("/skills/curriculum/warmup", {
        subject: subjectKey, grade: gradeKey,
      });
      if (data.already_complete) {
        toast.success("All topics already cached!");
        return;
      }
      if (data.job) setWarmup(data.job);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start warmup");
    }
  };

  const openDesigner = () => {
    setDesignTitle(q.trim());
    setDesignKinds(["illustration", "quiz"]);
    setDesignOpen(true);
  };

  const submitDesign = async () => {
    const title = designTitle.trim();
    if (!title) return toast.error("Give the skill a name");
    if (!designKinds.length) return toast.error("Pick at least one type");
    setDesignBusy(true);
    try {
      const { data } = await api.post("/skills/curriculum/custom", {
        subject: subjectKey, grade: gradeKey, title, kinds: designKinds,
      });
      setTopicItems((cur) => [...cur, { name: data.skill.name, cached: false, custom: true, kinds: data.skill.kinds, creator_name: data.skill.creator_name }]);
      setDesignOpen(false);
      setQ("");
      toast.success("Skill saved to the shared library — dropping it onto your board…");
      // Immediately usable: auto-drop the new skill onto the Interactive Board.
      const meta = { subject: subjectKey, grade: gradeKey };
      if (designKinds.includes("illustration")) onVisual?.(title, meta, designKinds.includes("quiz"));
      if (designKinds.includes("quiz")) practiceTopic(title);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not design the skill");
    } finally { setDesignBusy(false); }
  };

  const practiceTopic = async (topic) => {
    if (busyTopic) return;
    setBusyTopic(topic);    try {
      const { data } = await api.post("/skills/curriculum/practice", {
        subject: subjectKey, grade: gradeKey, topic, count: 5,
      });
      if (data?.skill) {
        onPractice?.(data.skill, { subject: subjectKey, grade: gradeKey, topic });
        toast.success(data.cached ? "Loaded cached practice" : "AI generated 5 questions");
        // Mark this topic as cached locally so the ✓ appears immediately.
        setTopicItems((prev) => prev.map((t) => t.name === topic ? { ...t, cached: true } : t));
        setCachedCount((c) => c + (data.cached ? 0 : 1));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate practice");
    } finally {
      setBusyTopic(null);
    }
  };

  // ── UI ─────────────────────────────────────────────────────────────
  return (
    <div
      className="w-[300px] h-full bg-white border-r border-black/10 shadow-[8px_0_24px_-12px_rgba(0,0,0,0.15)] flex flex-col"
      data-testid="curriculum-skills-panel"
    >
      {/* Header */}
      <div className="px-3 py-2.5 border-b border-black/10 bg-[#0A0C10] text-white flex items-center gap-2">
        <Sparkles size={14} className="text-[#FFD600]" />
        <div className="flex-1 min-w-0">
          <div className="text-[9px] font-mono uppercase tracking-[0.22em] text-white/60">/ practice</div>
          <div className="text-sm font-bold truncate">Skills library</div>
        </div>
        <button onClick={onClose} data-testid="curriculum-close"
          className="text-white/60 hover:text-white p-1 rounded hover:bg-white/10">
          <X size={14} />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        {treeLoading && (
          <div className="p-4 text-xs text-neutral-500 inline-flex items-center gap-1.5">
            <Loader2 size={12} className="animate-spin" /> Loading catalog…
          </div>
        )}

        {/* Step 1 — Choose subject */}
        {!subjectKey && !treeLoading && (
          <div className="p-3 space-y-2" data-testid="curriculum-step-subject">
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-neutral-500 mb-1">
              1 · Choose subject
            </div>
            {(tree?.subjects || []).map((s) => (
              <button
                key={s.key}
                onClick={() => setSubjectKey(s.key)}
                data-testid={`curriculum-subject-${s.key}`}
                className="w-full text-left border border-neutral-200 hover:border-[#0A0C10] rounded-lg px-3 py-2.5 flex items-center gap-2.5 bg-white transition-colors"
              >
                <div
                  className="w-8 h-8 rounded-md flex items-center justify-center"
                  style={{ background: (s.color || SUBJECT_FALLBACK_COLORS[s.key] || "#7C3AED") + "20", color: s.color || SUBJECT_FALLBACK_COLORS[s.key] || "#7C3AED" }}
                >
                  <BookOpen size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-neutral-900 truncate">{s.label}</div>
                  <div className="text-[10px] font-mono text-neutral-500 mt-0.5">
                    {s.grades?.reduce((sum, g) => sum + (g.topic_count || 0), 0)} topics · {s.grades?.length || 0} grades
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Step 2 — Choose grade */}
        {subjectKey && !gradeKey && (
          <div className="p-3" data-testid="curriculum-step-grade">
            <button
              onClick={() => setSubjectKey(null)}
              className="text-[11px] text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1 mb-2"
              data-testid="curriculum-back-to-subject"
            >
              <ChevronLeft size={12} /> Back to subjects
            </button>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-neutral-500 mb-2">
              2 · Select grade — {subject?.label}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {(subject?.grades || []).map((g) => (
                <button
                  key={g.key}
                  onClick={() => setGradeKey(g.key)}
                  data-testid={`curriculum-grade-${g.key}`}
                  className="border border-neutral-200 hover:border-[#0A0C10] rounded-md px-2 py-2 text-left bg-white transition-colors"
                >
                  <div className="text-[10px] font-mono font-bold" style={{ color: subject?.color || "#7C3AED" }}>
                    {g.key}
                  </div>
                  <div className="text-xs font-semibold text-neutral-800 truncate">{g.label}</div>
                  <div className="text-[10px] text-neutral-500 mt-0.5">{g.topic_count} topics</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 3 — Topic list */}
        {subjectKey && gradeKey && (
          <div className="flex flex-col h-full" data-testid="curriculum-step-topics">
            <div className="px-3 pt-3 pb-2 border-b border-black/5 bg-white sticky top-0 z-10">
              <button
                onClick={() => { setGradeKey(null); setQ(""); }}
                className="text-[11px] text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1 mb-1"
                data-testid="curriculum-back-to-grade"
              >
                <ChevronLeft size={12} /> {subject?.label} · Change grade
              </button>
              <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-neutral-500 flex items-center gap-2">
                <span>3 · {grade?.label} — {topics.length} topics</span>
                {cachedCount > 0 && (
                  <span className="inline-flex items-center gap-0.5 text-[#059669] normal-case font-semibold tracking-normal">
                    <Check size={10} /> {cachedCount} instant
                  </span>
                )}
              </div>
            </div>

            {/* Warm-up progress / CTA */}
            {(topics.length > cachedCount || warmup) && (
              <div className="px-3 py-2 border-b border-[#FFD600]/20 bg-[#FFD600]/10">
                {warmup?.status === "running" ? (
                  <div data-testid="curriculum-warmup-progress" className="space-y-1.5">
                    <div className="flex items-center justify-between text-[10px] font-mono font-bold uppercase tracking-wider text-[#FFD600]">
                      <span className="inline-flex items-center gap-1">
                        <Loader2 size={10} className="animate-spin" /> Warming up · {warmup.done}/{warmup.total}
                      </span>
                      <span>{Math.round(((warmup.done || 0) / Math.max(1, warmup.total)) * 100)}%</span>
                    </div>
                    <div className="h-1.5 bg-[#FFD600]/20 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#FFD600] transition-all duration-500"
                        style={{ width: `${Math.round(((warmup.done || 0) / Math.max(1, warmup.total)) * 100)}%` }}
                      />
                    </div>
                    {warmup.current_topic && (
                      <div className="text-[10px] text-neutral-400 truncate">
                        · {warmup.current_topic}
                      </div>
                    )}
                  </div>
                ) : warmup?.status === "done" ? (
                  <div className="text-[11px] text-[#34D399] inline-flex items-center gap-1 font-semibold">
                    <Check size={11} strokeWidth={3} /> All topics warmed up
                  </div>
                ) : (
                  <button
                    onClick={startWarmup}
                    data-testid="curriculum-warmup-start"
                    className="w-full inline-flex items-center justify-center gap-1.5 text-[11px] font-bold text-[#0A0C10] bg-[#FFD600] hover:bg-[#FFC600] rounded-md px-3 py-1.5 transition-colors"
                    style={{ color: "#0A0C10" }}
                    title={`Generate the ${topics.length - cachedCount} remaining topics so they load instantly for everyone`}
                  >
                    <Zap size={12} /> <span style={{ color: "#0A0C10" }}>Warm up whole grade · {topics.length - cachedCount} left</span>
                  </button>
                )}
              </div>
            )}

            {/* Search box */}
            <div className="px-3 py-2 border-b border-black/5">
              <div className="flex items-center gap-1.5 bg-neutral-100 border border-neutral-200 rounded-md px-2 py-1.5">
                <Search size={12} className="text-neutral-400 shrink-0" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={`Search ${subject?.label?.toLowerCase() || "topic"} skills…`}
                  data-testid="curriculum-search-input"
                  className="flex-1 min-w-0 bg-transparent text-xs outline-none text-neutral-800 placeholder:text-neutral-400"
                />
                {q && (
                  <button
                    onClick={() => setQ("")}
                    className="text-neutral-400 hover:text-neutral-700 p-0.5 rounded"
                    data-testid="curriculum-search-clear"
                  >
                    <X size={10} />
                  </button>
                )}
              </div>
            </div>

            {/* Topic rows */}
            <div className="flex-1 overflow-y-auto">
              {topicsLoading && (
                <div className="p-4 text-xs text-neutral-500 inline-flex items-center gap-1.5">
                  <Loader2 size={12} className="animate-spin" /> Loading topics…
                </div>
              )}
              {!topicsLoading && filteredTopicItems.length === 0 && (
                <div className="p-6 text-center text-xs text-neutral-500" data-testid="curriculum-no-results">
                  <div>{q ? `No skill matches "${q}".` : "No topics available."}</div>
                  <button
                    onClick={openDesigner}
                    data-testid="design-skill-empty-btn"
                    className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#7C3AED] text-white text-xs font-bold hover:bg-[#6D28D9]"
                  >
                    <Wand2 size={12} /> Design this skill
                  </button>
                </div>
              )}
              {!topicsLoading && filteredTopicItems.map((item, i) => {
                const topic = item.name;
                const cached = item.cached;
                const busy = busyTopic === topic;
                return (
                  <button
                    key={`${topic}-${i}`}
                    onClick={() => setChoiceTopic(topic)}
                    disabled={!!busyTopic}
                    data-testid={`curriculum-topic-${i}`}
                    className={`w-full text-left px-3 py-2.5 border-b border-black/5 flex items-center gap-2 transition-colors ${
                      busy ? "bg-[#F5F3FF]" : "hover:bg-neutral-50"
                    } ${busyTopic && !busy ? "opacity-50" : ""}`}
                  >
                    <div
                      className="w-1.5 h-1.5 rounded-full shrink-0"
                      style={{ background: subject?.color || SUBJECT_FALLBACK_COLORS[subjectKey] || "#7C3AED" }}
                    />
                    <div className="flex-1 min-w-0 text-xs font-medium text-neutral-800 leading-snug break-words">
                      {topic}
                      {item.custom && (
                        <span
                          title={`Designed by ${item.creator_name || "a teacher"} — shared with everyone teaching this grade & subject`}
                          className="ml-1.5 inline-flex items-center gap-0.5 text-[8px] font-mono font-bold uppercase tracking-wider text-[#7C3AED] bg-[#7C3AED]/10 border border-[#7C3AED]/30 rounded-full px-1.5 py-0.5 align-middle"
                          data-testid={`curriculum-custom-badge-${i}`}
                        >
                          <Wand2 size={7} /> designed
                        </span>
                      )}
                    </div>
                    {busy ? (
                      <Loader2 size={12} className="animate-spin text-[#7C3AED] shrink-0" />
                    ) : cached ? (
                      <span
                        title="Cached · instant load"
                        className="inline-flex items-center gap-0.5 text-[9px] font-mono font-bold text-[#059669] shrink-0"
                        data-testid={`curriculum-topic-cached-${i}`}
                      >
                        <Check size={11} strokeWidth={3} />
                      </span>
                    ) : (
                      <Sparkles size={11} className="text-neutral-300 group-hover:text-[#7C3AED] shrink-0" />
                    )}
                  </button>
                );
              })}
              {!topicsLoading && (
                <div className="p-2 border-t border-neutral-100">
                  <button
                    onClick={openDesigner}
                    data-testid="design-skill-row-btn"
                    className="w-full inline-flex items-center justify-center gap-2 px-3 py-3 rounded-lg bg-gradient-to-r from-[#7C3AED] to-[#0EA5E9] text-white text-sm font-bold shadow-[0_0_16px_rgba(124,58,237,0.45)] hover:shadow-[0_0_24px_rgba(14,165,233,0.55)] hover:scale-[1.02] transition-all"
                  >
                    <Plus size={15} /> Create a Custom Diagram
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer hint */}
      {subjectKey && gradeKey && (
        <div className="border-t border-black/10 px-3 py-2 text-[10px] text-neutral-500 bg-neutral-50">
          Click a topic — choose a Visual (AI illustration) or an Activity (5 practice questions).
        </div>
      )}

      {/* Design-a-skill modal */}
      {designOpen && (
        <div
          className="fixed inset-0 z-[95] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={(e) => { if (e.target === e.currentTarget && !designBusy) setDesignOpen(false); }}
          data-testid="design-skill-modal"
        >
          <div className="w-full max-w-sm bg-white rounded-xl border border-black/10 shadow-2xl p-5">
            <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#7C3AED] font-bold inline-flex items-center gap-1.5">
              <Wand2 size={11} /> / design a skill
            </div>
            <div className="font-display font-bold text-lg tracking-tight mt-1">
              {subject?.label} · {grade?.label}
            </div>
            <p className="text-[11px] text-neutral-500 mt-1">Added to the shared library — visible to every teacher of this grade &amp; subject.</p>
            <input
              autoFocus
              value={designTitle}
              onChange={(e) => setDesignTitle(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submitDesign()}
              placeholder="Skill name — e.g. Balancing chemical equations"
              data-testid="design-skill-title"
              className="mt-3 w-full text-sm border border-neutral-300 rounded-md px-3 py-2 outline-none focus:border-[#7C3AED]"
            />
            <div className="mt-3 space-y-2">
              <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">What should it include?</div>
              {[["illustration", "Illustration — AI visual for the board"], ["quiz", "Quiz — 5 practice questions"]].map(([k, lbl]) => (
                <label key={k} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`design-kind-${k}`}>
                  <input
                    type="checkbox"
                    checked={designKinds.includes(k)}
                    onChange={() => setDesignKinds((cur) => (cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]))}
                  />
                  {lbl}
                </label>
              ))}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setDesignOpen(false)} disabled={designBusy} className="px-4 py-2 text-xs border border-neutral-300 rounded-md hover:border-black">Cancel</button>
              <button
                onClick={submitDesign}
                disabled={designBusy}
                data-testid="design-skill-submit"
                className="px-5 py-2 text-xs font-bold bg-[#7C3AED] text-white rounded-md hover:bg-[#6D28D9] disabled:opacity-60 inline-flex items-center gap-1.5"
              >
                {designBusy ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />} Add to library
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SVG-vs-Activity choice modal */}
      {choiceTopic && (
        <div
          className="fixed inset-0 z-[95] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4"
          data-testid="asset-choice-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setChoiceTopic(null); }}
        >
          <div className="w-full max-w-md bg-[#0B0F17] border border-[#7C3AED]/40 rounded-2xl shadow-[0_0_60px_-16px_rgba(124,58,237,0.7)] overflow-hidden text-white">
            <div className="px-5 py-4 border-b border-white/10 bg-gradient-to-r from-[#7C3AED]/15 to-transparent flex items-center justify-between">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.26em] text-[#C084FC] font-bold">/ Generate asset</div>
                <div className="font-display font-bold text-base tracking-tight mt-0.5 line-clamp-2">{choiceTopic}</div>
              </div>
              <button onClick={() => setChoiceTopic(null)} data-testid="asset-choice-close" className="text-white/60 hover:text-white p-1 rounded hover:bg-white/10"><X size={15} /></button>
            </div>
            <div className="p-4 space-y-2.5">
              <button
                onClick={() => { const t = choiceTopic; setChoiceTopic(null); onVisual?.(t, { subject: subjectKey, grade: gradeKey }); }}
                data-testid="asset-choice-visual"
                className="w-full text-left rounded-xl border border-[#22D3EE]/40 bg-[#22D3EE]/8 hover:bg-[#22D3EE]/15 px-4 py-3.5 transition-colors"
              >
                <div className="font-bold text-sm text-[#22D3EE]">Visual — AI illustration</div>
                <div className="text-xs text-white/60 mt-0.5">Background-less, resizeable image of the topic placed on the board.</div>
              </button>
              <button
                onClick={() => { const t = choiceTopic; setChoiceTopic(null); practiceTopic(t); }}
                data-testid="asset-choice-activity"
                className="w-full text-left rounded-xl border border-[#7C3AED]/40 bg-[#7C3AED]/8 hover:bg-[#7C3AED]/15 px-4 py-3.5 transition-colors"
              >
                <div className="font-bold text-sm text-[#C084FC]">Activity — 5 practice questions</div>
                <div className="text-xs text-white/60 mt-0.5">Interactive MCQ widget students can answer live.</div>
              </button>
              <button
                onClick={() => { const t = choiceTopic; setChoiceTopic(null); onVisual?.(t, { subject: subjectKey, grade: gradeKey }, true); practiceTopic(t); }}
                data-testid="asset-choice-both"
                className="w-full text-left rounded-xl border border-[#FFD600]/40 bg-[#FFD600]/8 hover:bg-[#FFD600]/15 px-4 py-3.5 transition-colors"
              >
                <div className="font-bold text-sm text-[#FFD600]">Both — activity + visual beside it</div>
                <div className="text-xs text-white/60 mt-0.5">Practice widget with the AI visual placed next to it.</div>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
